/**
 * Socket.IO Service
 *
 * Handles WebSocket connection for real-time matchmaking and game updates
 */

import { io } from 'socket.io-client';

let socket = null;
let socketIdentity = null;

export const connectSocket = (userId, token) => {
  const getDefaultSocketUrl = () => {
    if (typeof window !== 'undefined' && window.location) {
      const hostname = window.location.hostname || 'localhost';
      const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
      const isPrivateLan =
        /^10\./.test(hostname) ||
        /^192\.168\./.test(hostname) ||
        /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);
      const protocol = (isLocal || isPrivateLan) ? 'http:' : (window.location.protocol || 'http:');
      const origin = window.location.origin || `${protocol}//${window.location.host}`;
      const port = window.location.port || '';
      const isDevFrontendPort = port === '3000' || port === '3001';
      if (isLocal) {
        return `${protocol}//${hostname}:5000`;
      }
      if (isPrivateLan && isDevFrontendPort) {
        return `${protocol}//${hostname}:5000`;
      }
      return origin.replace(/\/+$/, '');
    }
    return 'http://localhost:5000';
  };

  const resolveSocketUrl = () => {
    const defaultUrl = getDefaultSocketUrl();
    const envUrl = process.env.REACT_APP_SOCKET_URL;

    // Override localhost env when accessed via LAN IP on mobile
    if (envUrl && typeof window !== 'undefined' && window.location) {
      const pageHost = window.location.hostname;
      const pageIsLocal = pageHost === 'localhost' || pageHost === '127.0.0.1';
      const envIsLocal = /localhost|127\.0\.0\.1/i.test(envUrl);

      if (!pageIsLocal && envIsLocal) {
        return defaultUrl;
      }
    }

    return envUrl || defaultUrl;
  };

  const SOCKET_URL = resolveSocketUrl();
  const nextIdentity = `${SOCKET_URL}|${userId || ''}`;

  if (socket && socketIdentity === nextIdentity) {
    socket.auth = { token, userId };
    if (socket.connected) {
      // Already connected — just re-authenticate
      socket.emit('join', { userId, token });
      return socket;
    }
    if (socket.active) {
      // Reconnection is in progress — don't interfere, let it finish
      return socket;
    }
    // Socket permanently disconnected (exhausted retries) — recreate it
    console.log('🔄 Socket: Permanently disconnected, recreating...');
    socket.disconnect();
    socket = null;
    socketIdentity = null;
  }

  if (socket) {
    socket.disconnect();
    socket = null;
  }

  console.log(`🔗 Socket: Connecting to ${SOCKET_URL} as user ${userId}`);

  socket = io(SOCKET_URL, {
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: Infinity, // Never permanently give up
    transports: ['websocket', 'polling'],
    auth: {
      token: token,
      userId: userId,
    },
    upgrade: true,
    timeout: 20000,
  });
  socketIdentity = nextIdentity;

  // Authenticate after connection
  socket.on('connect', () => {
    console.log('✓ Socket connected successfully');
    console.log(`  Transport: ${socket.io.engine.transport.name}`);
    socket.emit('join', { userId, token });
  });

  socket.on('user:balance_updated', (data) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('user:balanceUpdated', { detail: data }));
    }
  });

  socket.on('disconnect', () => {
    console.log('✗ Socket disconnected');
  });

  socket.on('connect_error', (error) => {
    console.error('❌ Socket connection error:', error.message);
  });

  socket.on('reconnecting', (attemptNumber) => {
    console.log(`🔄 Socket reconnecting (attempt ${attemptNumber})`);
  });

  socket.on('reconnect', () => {
    console.log('✓ Socket reconnected');
    // Re-authenticate after reconnection so socket.userId is set on the server
    socket.emit('join', { userId, token });
  });

  socket.on('reconnect_failed', () => {
    console.error('❌ Socket: Reconnection failed permanently');
  });

  return socket;
};

export const getSocket = () => socket;

export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
    socketIdentity = null;
  }
};

/**
 * Flappy Simultaneous (beta) methods
 * - Deterministic seed shared via server
 * - Input-only sync (FLAP)
 */
export const flappySimSocket = {
  join: (matchId) => {
    if (socket) socket.emit('flappy:sim:join', { matchId });
  },

  ping: (clientTime = Date.now()) => {
    if (socket) socket.emit('flappy:sim:ping', { clientTime });
  },

  sendInput: ({ matchId, frame, type = 'FLAP', clientTime = Date.now() }) => {
    if (!socket) return;
    socket.emit('flappy:sim:input', { matchId, frame, type, clientTime });
  },

  onClockSync: (callback) => {
    if (socket) socket.on('flappy:sim:clock-sync', callback);
  },

  onMatchStart: (callback) => {
    if (socket) socket.on('flappy:sim:match-start', callback);
  },

  onOpponentInput: (callback) => {
    if (socket) socket.on('flappy:sim:opponent-input', callback);
  },

  onPlayerJoined: (callback) => {
    if (socket) socket.on('flappy:sim:player-joined', callback);
  },

  onPong: (callback) => {
    if (socket) socket.on('flappy:sim:pong', callback);
  },

  onEnded: (callback) => {
    if (socket) socket.on('flappy:sim:ended', callback);
  },

  removeListeners: () => {
    if (!socket) return;
    socket.off('flappy:sim:clock-sync');
    socket.off('flappy:sim:match-start');
    socket.off('flappy:sim:opponent-input');
    socket.off('flappy:sim:player-joined');
    socket.off('flappy:sim:pong');
    socket.off('flappy:sim:ended');
  },
};

/**
 * Flappy Duel (turn-based) live spectate helpers
 */
export const flappyDuelSocket = {
  join: (matchId) => {
    if (socket) socket.emit('flappy:duel:join', { matchId });
  },

  sendFlap: ({ matchId, roundNumber = 1, tMs }) => {
    if (!socket) return;
    socket.emit('flappy:duel:flap', { matchId, roundNumber, tMs });
  },

  onJoined: (callback) => {
    if (socket) socket.on('flappy:duel:joined', callback);
  },

  onOpponentFlap: (callback) => {
    if (socket) socket.on('flappy:duel:opponent-flap', callback);
  },

  onRunEnded: (callback) => {
    if (socket) socket.on('flappy:duel:run-ended', callback);
  },

  onAccepted: (callback) => {
    if (socket) socket.on('flappy:duel:accepted', callback);
  },

  onStarted: (callback) => {
    if (socket) socket.on('flappy:duel:started', callback);
  },

  onRechallengeCreated: (callback) => {
    if (socket) socket.on('flappy:duel:rechallenge-created', callback);
  },

  onRematchCreated: (callback) => {
    if (socket) socket.on('flappy:duel:rechallenge-created', callback);
  },

  removeListeners: () => {
    if (!socket) return;
    socket.off('flappy:duel:joined');
    socket.off('flappy:duel:opponent-flap');
    socket.off('flappy:duel:run-ended');
    socket.off('flappy:duel:accepted');
    socket.off('flappy:duel:started');
    socket.off('flappy:duel:rechallenge-created');
  },
};

/**
 * Reaction Duel helpers
 */
export const reactionDuelSocket = {
  join: (matchId) => {
    if (socket) socket.emit('reaction:duel:join', { matchId });
  },

  onJoined: (callback) => {
    if (socket) socket.on('reaction:duel:joined', callback);
  },

  onMatchUpdate: (callback) => {
    if (socket) socket.on('reaction:match:update', callback);
  },

  onRematchCreated: (callback) => {
    if (socket) socket.on('reaction:duel:rematch-created', callback);
  },

  removeListeners: () => {
    if (!socket) return;
    socket.off('reaction:duel:joined');
    socket.off('reaction:match:update');
    socket.off('reaction:duel:rematch-created');
  },
};

/**
 * Chess Duel helpers
 */
export const chessDuelSocket = {
  join: (matchId) => {
    if (socket) socket.emit('chess:duel:join', { matchId });
  },

  sendChat: ({ matchId, text }, cb) => {
    if (socket?.connected) {
      socket.emit('chess:chat:send', { matchId, text }, cb);
    } else if (cb) {
      cb({ ok: false, error: 'Connection lost. Please wait for reconnection.' });
    }
  },

  submitMove: (matchId, { from, to, promotion = 'q' }, cb) => {
    if (socket?.connected) {
      socket.emit('chess:move', { matchId, from, to, promotion }, cb);
    } else if (cb) {
      cb({ ok: false, error: 'Connection lost. Please wait for reconnection.' });
    }
  },

  onJoined: (callback) => {
    if (socket) socket.on('chess:duel:joined', callback);
  },

  onMatchUpdate: (callback) => {
    if (socket) socket.on('chess:match:update', callback);
  },

  // Fired by the server BEFORE the DB transaction commits.
  // Gives the opponent instant visual feedback (~0ms) while the
  // authoritative chess:match:update follows once the DB commits.
  onMoveRelay: (callback) => {
    if (socket) socket.on('chess:move:relay', callback);
  },

  onChatHistory: (callback) => {
    if (socket) socket.on('chess:chat:history', callback);
  },

  onChatMessage: (callback) => {
    if (socket) socket.on('chess:chat:new', callback);
  },

  onRematchCreated: (callback) => {
    if (socket) socket.on('chess:rematch:created', callback);
  },

  removeListeners: () => {
    if (!socket) return;
    socket.off('chess:duel:joined');
    socket.off('chess:match:update');
    socket.off('chess:move:relay');
    socket.off('chess:chat:history');
    socket.off('chess:chat:new');
    socket.off('chess:rematch:created');
    socket.off('chess:move');
  },
};

/**
 * Chess Tournament helpers
 */
export const chessTournamentSocket = {
  join: (tournamentId) => {
    if (socket) socket.emit('chess:tournament:chat:join', { tournamentId });
  },

  sendChat: ({ tournamentId, text }, cb) => {
    if (socket) socket.emit('chess:tournament:chat:send', { tournamentId, text }, cb);
  },

  onChatHistory: (callback) => {
    if (socket) socket.on('chess:tournament:chat:history', callback);
  },

  onChatMessage: (callback) => {
    if (socket) socket.on('chess:tournament:chat:new', callback);
  },

  removeListeners: () => {
    if (!socket) return;
    socket.off('chess:tournament:chat:history');
    socket.off('chess:tournament:chat:new');
  },
};


/**
 * Checkers Duel helpers
 */
export const checkersDuelSocket = {
  join: (matchId) => {
    if (socket) socket.emit('checkers:duel:join', { matchId });
  },

  onJoined: (callback) => {
    if (socket) socket.on('checkers:duel:joined', callback);
  },

  onMatchUpdate: (callback) => {
    if (socket) socket.on('checkers:match:update', callback);
  },

  removeListeners: () => {
    if (!socket) return;
    socket.off('checkers:duel:joined');
    socket.off('checkers:match:update');
  },
};

/**
 * Draughts (International 10×10) Duel helpers
 */
export const draughtsDuelSocket = {
  join: (matchId) => {
    if (socket) socket.emit('draughts:duel:join', { matchId });
  },

  sendChat: ({ matchId, text }, cb) => {
    if (socket) socket.emit('draughts:chat:send', { matchId, text }, cb);
  },

  onJoined: (callback) => {
    if (socket) socket.on('draughts:duel:joined', callback);
  },

  onMatchUpdate: (callback) => {
    if (socket) socket.on('draughts:match:update', callback);
  },

  onChatHistory: (callback) => {
    if (socket) socket.on('draughts:chat:history', callback);
  },

  onChatMessage: (callback) => {
    if (socket) socket.on('draughts:chat:new', callback);
  },

  onRematchCreated: (callback) => {
    if (socket) socket.on('draughts:rematch:created', callback);
  },

  onRematchAccepted: (callback) => {
    if (socket) socket.on('draughts:rematch:accepted', callback);
  },

  removeListeners: () => {
    if (!socket) return;
    socket.off('draughts:duel:joined');
    socket.off('draughts:match:update');
    socket.off('draughts:chat:history');
    socket.off('draughts:chat:new');
    socket.off('draughts:rematch:created');
    socket.off('draughts:rematch:accepted');
  },
};

/**
 * WHOT Duel socket (minimal relay)
 */
export const whotDuelSocket = {
  join: (challengeId, userId) => {
    if (socket) socket.emit('whot:join', { challengeId, userId });
  },

  onJoined: (callback) => {
    if (socket) socket.on('whot:joined', callback);
  },

  onStart: (callback) => {
    if (socket) socket.on('whot:start', callback);
  },

  playCard: ({ challengeId, userId, card, meta = {} }) => {
    if (!socket) return;
    socket.emit('whot:play', { challengeId, userId, card, meta });
  },

  onOpponentPlay: (callback) => {
    if (socket) socket.on('whot:play', callback);
  },

  drawCard: ({ challengeId, userId, meta = {} }) => {
    if (!socket) return;
    socket.emit('whot:draw', { challengeId, userId, meta });
  },

  onOpponentDraw: (callback) => {
    if (socket) socket.on('whot:draw', callback);
  },

  callShape: ({ challengeId, userId, shape, meta = {} }) => {
    if (!socket) return;
    socket.emit('whot:call_shape', { challengeId, userId, shape, meta });
  },

  onOpponentCallShape: (callback) => {
    if (socket) socket.on('whot:call_shape', callback);
  },

  removeListeners: () => {
    if (!socket) return;
    socket.off('whot:joined');
    socket.off('whot:start');
    socket.off('whot:play');
    socket.off('whot:draw');
    socket.off('whot:call_shape');
  },
};

/**
 * Matchmaking methods
 */
export const matchmakingSocket = {
  joinQueue: (wager) => {
    if (socket) socket.emit('queue:join', { wager });
  },

  leaveQueue: () => {
    if (socket) socket.emit('queue:leave');
  },

  onQueueUpdate: (callback) => {
    if (socket) socket.on('queue:update', callback);
  },

  onQueueJoined: (callback) => {
    if (socket) socket.on('queue:joined', callback);
  },

  onGameStart: (callback) => {
    if (socket) socket.on('game:start', callback);
  },

  removeQueueUpdate: () => {
    if (socket) socket.off('queue:update');
  },
};

/**
 * Game methods
 */
export const gameSocket = {
  joinGame: (gameId) => {
    if (socket) socket.emit('game:joined', { gameId });
  },

  roll: (gameId) => {
    if (socket) socket.emit('game:roll', { gameId });
  },

  onRolled: (callback) => {
    if (socket) socket.on('game:rolled', callback);
  },

  onGameResult: (callback) => {
    if (socket) socket.on('game:result', callback);
  },

  onOpponentJoined: (callback) => {
    if (socket) socket.on('opponent:joined', callback);
  },

  removeGameListeners: () => {
    if (socket) {
      socket.off('game:rolled');
      socket.off('game:result');
      socket.off('opponent:joined');
    }
  },
};

/**
 * Chat methods - including typing indicators
 */
export const chatSocket = {
  startTyping: (conversationPartnerId) => {
    if (socket) socket.emit('chat:typing', { recipientId: conversationPartnerId });
  },

  stopTyping: (conversationPartnerId) => {
    if (socket) socket.emit('chat:stop-typing', { recipientId: conversationPartnerId });
  },

  onUserTyping: (callback) => {
    if (socket) socket.on('chat:user-typing', callback);
  },

  onUserStoppedTyping: (callback) => {
    if (socket) socket.on('chat:user-stopped-typing', callback);
  },

  removeTypingListeners: () => {
    if (socket) {
      socket.off('chat:user-typing');
      socket.off('chat:user-stopped-typing');
    }
  },
};

/**
 * Error handling
 */
export const onSocketError = (callback) => {
  if (socket) socket.on('error', callback);
};

export default socket;
